import type { Address, Coordinates } from '../types';
import { fetchBanMunicipalities, parseContext, type BanFeature } from '../hooks/useBanAutocomplete';
import { isFranceCountry } from './countries';

/**
 * Fold accents, case, punctuation and the St/Ste abbreviations into a comparable key,
 * so "St Etienne", "Saint-Étienne" and "saint etienne" all collapse to "saint-etienne".
 *
 * Also drops a trailing "Arrondissement" word: BAN's official name for a Paris/Lyon/Marseille
 * district is e.g. "Paris 6e Arrondissement", but venues commonly store just "Paris 6e" — which
 * already fully specifies the district, unlike a bare "Lyon". Only the word is dropped, not the
 * ordinal, so "lyon" still normalises differently from "lyon-3e" and stays rejected as before.
 */
function normalizeCommune(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/^st[\s-]+/, 'saint-')
    .replace(/^ste[\s-]+/, 'sainte-')
    .replace(/['’_\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .replace(/-arrondissement$/, '');
}

/**
 * The postal "CEDEX" routing suffix (optionally followed by a distribution number, e.g.
 * "Lyon Cedex 03") is not part of the commune name — BAN has no entry for it — so it must be
 * stripped before the query and before the exact-match comparison below, or every cedex address
 * is silently rejected by the safety check.
 */
function stripCedex(value: string): string {
  return value.replace(/\bcedex\b\s*\d*\s*$/i, '').trim();
}

/** French postcodes are 5 digits; overseas départements use a 3-digit prefix. */
function isPostcode(zip: string): boolean {
  return /^\d{5}$/.test(zip);
}

/**
 * Resolve an address to its commune centroid, but only when there is no doubt about which
 * commune it is: the name must match exactly (once normalised). If more than one commune
 * shares that name, the stored department is used to break the tie; a name that's already
 * unique nationally is resolved without needing the department at all. Ambiguity that survives
 * both checks (there are nine communes named Sainte-Marie) returns null — a grey map beats a
 * plausible-looking wrong pin.
 */
export async function geocodeVenueAddress(
  address: Address | undefined,
  signal?: AbortSignal
): Promise<Coordinates | null> {
  const rawCity = address?.city?.trim();
  // BAN only covers France, so anything else is left ungeocoded. A blank country still counts
  // as France — the uniqueness rule below is the real guard. Shared with the autocomplete.
  if (!rawCity || !isFranceCountry(address?.country)) return null;
  const city = stripCedex(rawCity);
  if (!city) return null; // city field was "Cedex" alone — nothing left to search on

  const zip = address?.zip_code?.trim();
  let features: BanFeature[];
  try {
    features = await fetchBanMunicipalities(city, {
      // Full-token scoring, not prefix matching: we are resolving, not autocompleting.
      autocomplete: false,
      limit: 20,
      // Applied server-side and strictly. Never re-check it against properties.postcode:
      // BAN indexes every postcode of a commune but reports only the primary one, so
      // comparing would reject Lyon, Paris, Marseille and every other multi-postcode city.
      postcode: zip && isPostcode(zip) ? zip : undefined,
      signal,
    });
  } catch {
    return null; // includes aborts — this is opportunistic, never worth surfacing
  }

  const wantedCity = normalizeCommune(city);
  const wantedDept = address?.administrative_region_1?.trim();
  const wantedDeptKey = wantedDept ? normalizeCommune(wantedDept) : null;

  // Several postcodes can map to one commune (and arrondissements share a name stem),
  // so dedupe on the INSEE code before judging ambiguity. Department is *not* applied in this
  // pass — see below.
  const byName = new Map<string, { coords: Coordinates; deptKeys: string[] }>();

  for (const f of features) {
    const props = f.properties;
    const name = props.city || props.name || '';
    // Exact normalised equality is the safety property: it separates "Lyon" from
    // "Lyon 3e Arrondissement" and rejects near-misses like "Sainte-Marie-la-Mer".
    if (normalizeCommune(name) !== wantedCity) continue;

    const coords = f.geometry?.coordinates;
    if (!coords || coords.length < 2) continue;
    const [longitude, latitude] = coords;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;

    const { deptName } = parseContext(props.context || '');
    const deptCode = (props.depcode ?? (props.context || '').split(', ')[0] ?? '').trim();
    // Users store either the department name or its number, so keep both as candidate keys.
    const deptKeys = [deptName, deptCode].filter(Boolean).map(normalizeCommune);

    const key = props.citycode || normalizeCommune(name);
    if (!byName.has(key)) {
      byName.set(key, {
        coords: { latitude: Math.round(latitude * 1e6) / 1e6, longitude: Math.round(longitude * 1e6) / 1e6 },
        deptKeys,
      });
    }
  }

  // A city name that matches exactly one commune nationally is unambiguous on its own — the
  // stored department can only narrow further, never overrule this. So a stale or mistyped
  // department (region-reform renames, typos) no longer discards an otherwise-certain match.
  if (byName.size === 1) return byName.values().next().value!.coords;
  if (byName.size === 0 || !wantedDeptKey) return null;

  const byDept = [...byName.values()].filter(c => c.deptKeys.includes(wantedDeptKey));
  return byDept.length === 1 ? byDept[0].coords : null; // still ambiguous, or department didn't narrow it down
}
